import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import NavigationBreadcrumb from '../../components/navigation/NavigationBreadcrumb';
import NotificationIndicator from '../../components/navigation/NotificationIndicator';

import Button from '../../components/ui/Button';
import CaseFilters from './components/CaseFilters';
import CaseListTable from './components/CaseListTable';
import CaseDetailPanel from './components/CaseDetailPanel';
import CaseFormModal from './components/CaseFormModal';

const CaseManagement = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'en');
  const [direction, setDirection] = useState(() => localStorage.getItem('direction') || 'ltr');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    practiceArea: '',
    status: '',
    assignedLawyer: '',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    const savedDirection = localStorage.getItem('direction') || 'ltr';
    setLanguage(savedLanguage);
    setDirection(savedDirection);
    document.documentElement?.setAttribute('dir', savedDirection);
  }, []);

  const mockCases = [
    {
      id: 1,
      caseNumber: '2024-156',
      clientName: 'Mohammed Alami',
      clientId: 'client-1',
      practiceArea: language === 'ar' ? 'القانون التجاري' : language === 'fr' ? 'Droit commercial' : 'Commercial Law',
      caseType: language === 'ar' ? 'نزاع تجاري' : language === 'fr' ? 'Litige commercial' : 'Commercial Dispute',
      assignedLawyer: 'Ahmed Benali',
      status: 'litigation',
      openedDate: '2024-01-15',
      nextHearing: '2026-02-10',
      courtName: language === 'ar' ? 'محكمة الاستئناف بالدار البيضاء' : language === 'fr' ? 'Cour d\'appel de Casablanca' : 'Court of Appeal Casablanca',
      description: language === 'ar' 
        ? `نزاع تجاري بين شركتين حول عقد توريد بقيمة 2 مليون درهم. يدعي العميل أن الطرف الآخر لم يلتزم بشروط العقد وتسبب في خسائر مالية كبيرة.\n\nتم رفع الدعوى في يناير 2024 وجارٍ التحضير للجلسة الأولى.`
        : language === 'fr' ? `Litige commercial entre deux sociétés concernant un contrat de fourniture d'une valeur de 2 millions de dirhams. Le client affirme que l'autre partie n'a pas respecté les termes du contrat et a causé des pertes financières importantes.\n\nL'action a été intentée en janvier 2024 et la préparation de la première audience est en cours.`
        : `Commercial dispute between two companies regarding a supply contract worth 2 million dirhams. The client claims that the other party failed to comply with the contract terms and caused significant financial losses.\n\nThe lawsuit was filed in January 2024 and preparation for the first hearing is underway.`,
      legalNotes: language === 'ar'
        ? `المستندات المطلوبة:\n- نسخة من العقد الأصلي\n- إثباتات الدفع\n- المراسلات بين الطرفين\n- تقرير الخبير المحاسبي\n\nالاستراتيجية القانونية: التركيز على إثبات عدم الالتزام بالشروط التعاقدية والأضرار المترتبة.`
        : language === 'fr' ? `Documents requis:\n- Copie du contrat original\n- Preuves de paiement\n- Correspondances entre les parties\n- Rapport d'expert comptable\n\nStratégie juridique: Se concentrer sur la preuve du non-respect des conditions contractuelles et des dommages qui en résultent.`
        : `Required documents:\n- Copy of original contract\n- Payment proofs\n- Correspondence between parties\n- Accounting expert report\n\nLegal strategy: Focus on proving non-compliance with contractual terms and resulting damages.`,
      timeline: [
        {
          type: 'status',icon: 'FileText',
          title: language === 'ar' ? 'فتح القضية' : language === 'fr' ? 'Ouverture du dossier' : 'Case Opened',
          description: language === 'ar' ? 'تم فتح القضية وتعيين المحامي' : language === 'fr' ? 'Dossier ouvert et avocat assigné' : 'Case opened and lawyer assigned',date: '2024-01-15'
        },
        {
          type: 'document',icon: 'Upload',
          title: language === 'ar' ? 'تحميل المستندات' : language === 'fr' ? 'Documents téléchargés' : 'Documents Uploaded',
          description: language === 'ar' ? 'تم تحميل العقد والمستندات الداعمة' : language === 'fr' ? 'Contrat et documents justificatifs téléchargés' : 'Contract and supporting documents uploaded',date: '2024-01-20'
        },
        {
          type: 'hearing',icon: 'Calendar',
          title: language === 'ar' ? 'جلسة مجدولة' : language === 'fr' ? 'Audience programmée' : 'Hearing Scheduled',
          description: language === 'ar' ? 'تم تحديد موعد الجلسة الأولى' : language === 'fr' ? 'Première audience fixée' : 'First hearing date set',date: '2026-02-10'
        }
      ],
      documents: [
        {
          id: 1,
          name: language === 'ar' ? 'العقد التجاري.pdf' : language === 'fr' ? 'Contrat commercial.pdf' : 'Commercial Contract.pdf',size: '2.4 MB',uploadDate: '2024-01-20'
        },
        {
          id: 2,
          name: language === 'ar' ? 'إثباتات الدفع.pdf' : language === 'fr' ? 'Preuves de paiement.pdf' : 'Payment Proofs.pdf',size: '1.8 MB',uploadDate: '2024-01-22'
        },
        {
          id: 3,
          name: language === 'ar' ? 'تقرير الخبير.pdf' : language === 'fr' ? 'Rapport d\'expert.pdf' : 'Expert Report.pdf',
          size: '3.2 MB',
          uploadDate: '2024-01-25'
        }
      ],
      billing: {
        totalBilled: 45000,
        totalPaid: 30000,
        outstanding: 15000,
        invoices: [
          {
            id: 1,
            invoiceNumber: 'INV-2024-089',
            date: '2024-01-20',
            amount: 15000,
            status: 'paid'
          },
          {
            id: 2,
            invoiceNumber: 'INV-2024-112',
            date: '2024-02-15',
            amount: 15000,
            status: 'paid'
          },
          {
            id: 3,
            invoiceNumber: 'INV-2024-145',
            date: '2026-01-20',
            amount: 15000,
            status: 'pending'
          }
        ]
      }
    },
    {
      id: 2,
      caseNumber: '2024-178',
      clientName: 'Fatima Bennani',
      clientId: 'client-2',
      practiceArea: language === 'ar' ? 'قانون الأسرة' : language === 'fr' ? 'Droit de la famille' : 'Family Law',
      caseType: language === 'ar' ? 'طلاق' : language === 'fr' ? 'Divorce' : 'Divorce',
      assignedLawyer: 'Sara Idrissi',
      status: 'negotiation',
      openedDate: '2024-02-01',
      nextHearing: '2026-02-15',
      courtName: language === 'ar' ? 'المحكمة الابتدائية بالرباط' : language === 'fr' ? 'Tribunal de première instance de Rabat' : 'First Instance Court Rabat',
      description: language === 'ar'
        ? `قضية طلاق بالتراضي مع ترتيبات الحضانة والنفقة. الطرفان متعاونان ويسعيان للتوصل إلى اتفاق ودي.\n\nجارٍ التفاوض على شروط الحضانة والنفقة الشهرية.`
        : language === 'fr'
        ? `Divorce par consentement mutuel avec arrangements de garde et pension alimentaire. Les deux parties coopèrent et cherchent à parvenir à un accord amiable.\n\nNégociation en cours sur les conditions de garde et la pension mensuelle.`
        : `Divorce by mutual consent with custody and alimony arrangements. Both parties are cooperative and seeking an amicable agreement.\n\nNegotiations ongoing regarding custody terms and monthly support.`,
      legalNotes: language === 'ar'
        ? `النقاط الرئيسية:\n- حضانة الأطفال (3 أطفال)\n- النفقة الشهرية\n- تقسيم الممتلكات\n- حقوق الزيارة\n\nالوضع الحالي: جلسات التفاوض تسير بشكل إيجابي.`
        : language === 'fr'
        ? `Points clés:\n- Garde des enfants (3 enfants)\n- Pension alimentaire mensuelle\n- Partage des biens\n- Droits de visite\n\nStatut actuel: Les séances de négociation se déroulent positivement.`
        : `Key points:\n- Child custody (3 children)\n- Monthly alimony\n- Property division\n- Visitation rights\n\nCurrent status: Negotiation sessions proceeding positively.`,
      timeline: [
        {
          type: 'status',
          icon: 'FileText',
          title: language === 'ar' ? 'فتح القضية' : language === 'fr' ? 'Ouverture du dossier' : 'Case Opened',
          description: language === 'ar' ? 'تم فتح قضية الطلاق' : language === 'fr' ? 'Dossier de divorce ouvert' : 'Divorce case opened',
          date: '2024-02-01'
        },
        {
          type: 'status',
          icon: 'Users',
          title: language === 'ar' ? 'بدء المفاوضات' : language === 'fr' ? 'Début des négociations' : 'Negotiations Started',
          description: language === 'ar' ? 'بدأت جلسات التفاوض بين الطرفين' : language === 'fr' ? 'Séances de négociation entre les parties commencées' : 'Negotiation sessions between parties started',
          date: '2024-02-10'
        }
      ],
      documents: [
        {
          id: 1,
          name: language === 'ar' ? 'عقد الزواج.pdf' : language === 'fr' ? 'Acte de mariage.pdf' : 'Marriage Certificate.pdf',
          size: '1.2 MB',
          uploadDate: '2024-02-05'
        },
        {
          id: 2,
          name: language === 'ar' ? 'شهادات ميلاد الأطفال.pdf' : language === 'fr' ? 'Actes de naissance des enfants.pdf' : 'Children Birth Certificates.pdf',
          size: '0.8 MB',
          uploadDate: '2024-02-05'
        }
      ],
      billing: {
        totalBilled: 25000,
        totalPaid: 25000,
        outstanding: 0,
        invoices: [
          {
            id: 1,
            invoiceNumber: 'INV-2024-095',
            date: '2024-02-05',
            amount: 25000,
            status: 'paid'
          }
        ]
      }
    },
    {
      id: 3,
      caseNumber: '2024-203',
      clientName: 'Hassan Tazi',
      clientId: 'client-3',
      practiceArea: language === 'ar' ? 'القانون العقاري' : language === 'fr' ? 'Droit immobilier' : 'Real Estate Law',
      caseType: language === 'ar' ? 'نزاع ملكية' : language === 'fr' ? 'Litige de propriété' : 'Property Dispute',
      assignedLawyer: 'Youssef Mansouri',
      status: 'investigation',
      openedDate: '2024-03-10',
      nextHearing: '2026-03-05',
      courtName: language === 'ar' ? 'المحكمة الابتدائية بمراكش' : language === 'fr' ? 'Tribunal de première instance de Marrakech' : 'First Instance Court Marrakech',
      description: language === 'ar'
        ? `نزاع حول ملكية عقار في مراكش. يدعي العميل ملكية الأرض بناءً على وثائق قديمة، بينما يطعن الطرف الآخر في صحة هذه الوثائق.\n\nجارٍ جمع الأدلة والوثائق التاريخية.`
        : language === 'fr' ? `Litige concernant la propriété d'un bien immobilier à Marrakech. Le client revendique la propriété du terrain sur la base de documents anciens, tandis que l'autre partie conteste la validité de ces documents.\n\nCollecte de preuves et de documents historiques en cours.`
        : `Dispute over property ownership in Marrakech. Client claims land ownership based on old documents, while the other party contests the validity of these documents.\n\nEvidence and historical documents collection in progress.`,
      legalNotes: language === 'ar'
        ? `المستندات المطلوبة:\n- سند الملكية الأصلي\n- الوثائق التاريخية\n- شهادات الشهود\n- تقرير الخبير العقاري\n\nالخطوات التالية: طلب تقرير خبرة عقارية من المحكمة.`
        : language === 'fr' ? `Documents requis:\n- Titre de propriété original\n- Documents historiques\n- Témoignages\n- Rapport d'expert immobilier\n\nProchaines étapes: Demander un rapport d'expertise immobilière au tribunal.`
        : `Required documents:\n- Original property title\n- Historical documents\n- Witness testimonies\n- Real estate expert report\n\nNext steps: Request real estate expertise report from court.`,
      timeline: [
        {
          type: 'status',
          icon: 'FileText',
          title: language === 'ar' ? 'فتح القضية' : language === 'fr' ? 'Ouverture du dossier' : 'Case Opened',
          description: language === 'ar' ? 'تم فتح قضية النزاع العقاري' : language === 'fr' ? 'Dossier de litige immobilier ouvert' : 'Property dispute case opened',
          date: '2024-03-10'
        },
        {
          type: 'document',
          icon: 'Upload',
          title: language === 'ar' ? 'تحميل الوثائق' : language === 'fr' ? 'Documents téléchargés' : 'Documents Uploaded',
          description: language === 'ar' ? 'تم تحميل سند الملكية والوثائق الداعمة' : language === 'fr' ? 'Titre de propriété et documents justificatifs téléchargés' : 'Property title and supporting documents uploaded',
          date: '2024-03-15'
        }
      ],
      documents: [
        {
          id: 1,
          name: language === 'ar' ? 'سند الملكية.pdf' : language === 'fr' ? 'Titre de propriété.pdf' : 'Property Title.pdf',
          size: '1.5 MB',
          uploadDate: '2024-03-15'
        },
        {
          id: 2,
          name: language === 'ar' ? 'الوثائق التاريخية.pdf' : language === 'fr' ? 'Documents historiques.pdf' : 'Historical Documents.pdf',
          size: '2.1 MB',
          uploadDate: '2024-03-18'
        }
      ],
      billing: {
        totalBilled: 35000,
        totalPaid: 20000,
        outstanding: 15000,
        invoices: [
          {
            id: 1,
            invoiceNumber: 'INV-2024-118',
            date: '2024-03-15',
            amount: 20000,
            status: 'paid'
          },
          {
            id: 2,
            invoiceNumber: 'INV-2024-156',
            date: '2026-01-25',
            amount: 15000,
            status: 'pending'
          }
        ]
      }
    },
    {
      id: 4,
      caseNumber: '2024-234',
      clientName: 'Amina Chraibi',
      clientId: 'client-4',
      practiceArea: language === 'ar' ? 'قانون العمل' : language === 'fr' ? 'Droit du travail' : 'Labor Law',
      caseType: language === 'ar' ? 'فصل تعسفي' : language === 'fr' ? 'Licenciement abusif' : 'Wrongful Termination',
      assignedLawyer: 'Fatima Zahra El Amrani',
      status: 'litigation',
      openedDate: '2024-04-05',
      nextHearing: '2026-02-20',
      courtName: language === 'ar' ? 'المحكمة الابتدائية بطنجة' : language === 'fr' ? 'Tribunal de première instance de Tanger' : 'First Instance Court Tangier',
      description: language === 'ar'
        ? `قضية فصل تعسفي من العمل. تدعي العميلة أنها فُصلت دون سبب مشروع وتطالب بالتعويض وإعادة التوظيف.\n\nتم رفع الدعوى وجارٍ التحضير للجلسة الأولى.`
        : language === 'fr'
        ? `Affaire de licenciement abusif. La cliente affirme avoir été licenciée sans motif légitime et demande une indemnisation et une réintégration.\n\nAction intentée et préparation de la première audience en cours.`
        : `Wrongful termination case. Client claims she was dismissed without legitimate cause and seeks compensation and reinstatement.\n\nLawsuit filed and first hearing preparation underway.`,
      legalNotes: language === 'ar'
        ? `المستندات المطلوبة:\n- عقد العمل\n- إشعار الفصل\n- كشوف الرواتب\n- شهادات الشهود\n\nالمطالبات: تعويض مالي + إعادة التوظيف أو تعويض إضافي.`
        : language === 'fr'
        ? `Documents requis:\n- Contrat de travail\n- Lettre de licenciement\n- Bulletins de salaire\n- Témoignages\n\nDemandes: Indemnisation financière + réintégration ou indemnité supplémentaire.`
        : `Required documents:\n- Employment contract\n- Termination notice\n- Pay slips\n- Witness testimonies\n\nClaims: Financial compensation + reinstatement or additional compensation.`,
      timeline: [
        {
          type: 'status',
          icon: 'FileText',
          title: language === 'ar' ? 'فتح القضية' : language === 'fr' ? 'Ouverture du dossier' : 'Case Opened',
          description: language === 'ar' ? 'تم فتح قضية الفصل التعسفي' : language === 'fr' ? 'Dossier de licenciement abusif ouvert' : 'Wrongful termination case opened',
          date: '2024-04-05'
        },
        {
          type: 'document',
          icon: 'Upload',
          title: language === 'ar' ? 'تحميل المستندات' : language === 'fr' ? 'Documents téléchargés' : 'Documents Uploaded',
          description: language === 'ar' ? 'تم تحميل عقد العمل والمستندات الداعمة' : language === 'fr' ? 'Contrat de travail et documents justificatifs téléchargés' : 'Employment contract and supporting documents uploaded',
          date: '2024-04-10'
        },
        {
          type: 'hearing',
          icon: 'Calendar',
          title: language === 'ar' ? 'جلسة مجدولة' : language === 'fr' ? 'Audience programmée' : 'Hearing Scheduled',
          description: language === 'ar' ? 'تم تحديد موعد الجلسة الأولى' : language === 'fr' ? 'Première audience fixée' : 'First hearing date set',
          date: '2026-02-20'
        }
      ],
      documents: [
        {
          id: 1,
          name: language === 'ar' ? 'عقد العمل.pdf' : language === 'fr' ? 'Contrat de travail.pdf' : 'Employment Contract.pdf',
          size: '0.9 MB',
          uploadDate: '2024-04-10'
        },
        {
          id: 2,
          name: language === 'ar' ? 'إشعار الفصل.pdf' : language === 'fr' ? 'Lettre de licenciement.pdf' : 'Termination Notice.pdf',
          size: '0.5 MB',
          uploadDate: '2024-04-10'
        },
        {
          id: 3,
          name: language === 'ar' ? 'كشوف الرواتب.pdf' : language === 'fr' ? 'Bulletins de salaire.pdf' : 'Pay Slips.pdf',
          size: '1.2 MB',
          uploadDate: '2024-04-12'
        }
      ],
      billing: {
        totalBilled: 30000,
        totalPaid: 15000,
        outstanding: 15000,
        invoices: [
          {
            id: 1,
            invoiceNumber: 'INV-2024-125',
            date: '2024-04-10',
            amount: 15000,
            status: 'paid'
          },
          {
            id: 2,
            invoiceNumber: 'INV-2024-167',
            date: '2026-01-27',
            amount: 15000,
            status: 'overdue'
          }
        ]
      }
    },
    {
      id: 5,
      caseNumber: '2024-267',
      clientName: 'Rachid Fassi',
      clientId: 'client-5',
      practiceArea: language === 'ar' ? 'القانون المدني' : language === 'fr' ? 'Droit civil' : 'Civil Law',
      caseType: language === 'ar' ? 'تعويض عن ضرر' : language === 'fr' ? 'Indemnisation pour préjudice' : 'Compensation for Damages',
      assignedLawyer: 'Karim Alaoui',
      status: 'intake',
      openedDate: '2026-01-20',
      nextHearing: '',
      courtName: '',
      description: language === 'ar'
        ? `قضية تعويض عن أضرار ناتجة عن حادث سير. يطالب العميل بتعويض عن الأضرار المادية والمعنوية.\n\nالقضية في مرحلة الاستقبال الأولي وجمع المستندات.`
        : language === 'fr' ? `Affaire d'indemnisation pour dommages résultant d'un accident de la route. Le client demande une indemnisation pour les dommages matériels et moraux.\n\nL'affaire est en phase de réception initiale et de collecte de documents.`
        : `Compensation case for damages resulting from a traffic accident. Client seeks compensation for material and moral damages.\n\nCase is in initial intake phase and document collection.`,
      legalNotes: language === 'ar'
        ? `المستندات المطلوبة:\n- تقرير الشرطة\n- التقارير الطبية\n- فواتير الإصلاح\n- شهادات الشهود\n\nالخطوات التالية: جمع جميع المستندات وتقييم الأضرار.`
        : language === 'fr'
        ? `Documents requis:\n- Rapport de police\n- Rapports médicaux\n- Factures de réparation\n- Témoignages\n\nProchaines étapes: Collecter tous les documents et évaluer les dommages.`
        : `Required documents:\n- Police report\n- Medical reports\n- Repair invoices\n- Witness testimonies\n\nNext steps: Collect all documents and assess damages.`,
      timeline: [
        {
          type: 'status',icon: 'FileText',
          title: language === 'ar' ? 'فتح القضية' : language === 'fr' ? 'Ouverture du dossier' : 'Case Opened',
          description: language === 'ar' ? 'تم فتح قضية التعويض' : language === 'fr' ? 'Dossier d\'indemnisation ouvert' : 'Compensation case opened',
          date: '2026-01-20'
        }
      ],
      documents: [
        {
          id: 1,
          name: language === 'ar' ? 'تقرير الشرطة.pdf' : language === 'fr' ? 'Rapport de police.pdf' : 'Police Report.pdf',
          size: '1.1 MB',
          uploadDate: '2026-01-22'
        }
      ],
      billing: {
        totalBilled: 0,
        totalPaid: 0,
        outstanding: 0,
        invoices: []
      }
    }
  ];

  const [filteredCases, setFilteredCases] = useState(mockCases);

  useEffect(() => {
    let result = mockCases;

    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      result = result?.filter(c => 
        c?.caseNumber?.toLowerCase()?.includes(searchLower) ||
        c?.clientName?.toLowerCase()?.includes(searchLower) ||
        c?.caseType?.toLowerCase()?.includes(searchLower)
      );
    }

    if (filters?.practiceArea) {
      result = result?.filter(c => c?.practiceArea?.toLowerCase()?.includes(filters?.practiceArea?.toLowerCase()));
    }

    if (filters?.status) {
      result = result?.filter(c => c?.status === filters?.status);
    }

    if (filters?.assignedLawyer) {
      result = result?.filter(c => c?.assignedLawyer?.toLowerCase()?.includes(filters?.assignedLawyer?.toLowerCase()));
    }

    if (filters?.dateFrom) {
      result = result?.filter(c => new Date(c.openedDate) >= new Date(filters.dateFrom));
    }

    if (filters?.dateTo) {
      result = result?.filter(c => new Date(c.openedDate) <= new Date(filters.dateTo));
    }

    setFilteredCases(result);
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      practiceArea: '',
      status: '',
      assignedLawyer: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleCaseSelect = (caseId) => {
    setSelectedCaseId(caseId);
    setShowDetailPanel(true);
  };

  const handleCloseDetailPanel = () => {
    setShowDetailPanel(false);
    setSelectedCaseId(null);
  };

  const handleCreateCase = () => {
    setEditingCase(null);
    setIsFormModalOpen(true);
  };

  const handleEditCase = () => {
    const caseToEdit = mockCases?.find(c => c?.id === selectedCaseId);
    setEditingCase(caseToEdit);
    setIsFormModalOpen(true);
  };

  const handleFormSubmit = (formData) => {
    console.log('Form submitted:', formData);
    setIsFormModalOpen(false);
    setEditingCase(null);
  };

  const handleScheduleHearing = () => {
    navigate('/calendar-scheduling');
  };

  const handleCreateInvoice = () => {
    navigate('/billing-invoice');
  };

  const selectedCase = mockCases?.find(c => c?.id === selectedCaseId);

  const pageTitle = {
    en: 'Case Management',
    fr: 'Gestion des dossiers',
    ar: 'إدارة القضايا'
  };

  const createCaseLabel = {
    en: 'Create New Case',
    fr: 'Créer un nouveau dossier',
    ar: 'إنشاء قضية جديدة'
  };

  const resultsLabel = {
    en: 'cases found',
    fr: 'dossiers trouvés',
    ar: 'قضية'
  };

  return (
    <div className="min-h-screen bg-background" style={{ direction }}>
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div
        className={`transition-all duration-250 pt-16 lg:pt-0 ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
        }`}
      >
        <header className="sticky top-0 z-40 bg-card border-b border-border shadow-elevation-sm px-6 md:px-8 lg:px-10 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-2">
                {pageTitle?.[language]}
              </h1>
              <NavigationBreadcrumb language={language} />
            </div>
            <div className="flex items-center gap-3">
              <NotificationIndicator language={language} />
              <Button
                variant="default"
                size="default"
                iconName="Plus"
                iconPosition="left"
                onClick={handleCreateCase}
                className="hidden sm:flex"
              >
                {createCaseLabel?.[language]}
              </Button>
              <Button
                variant="default"
                size="icon"
                iconName="Plus"
                onClick={handleCreateCase}
                className="sm:hidden"
              />
            </div>
          </div>
        </header>

        <main className="px-6 md:px-8 lg:px-10 py-6 md:py-8">
          <CaseFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            language={language}
            direction={direction}
          />

          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {filteredCases?.length} {resultsLabel?.[language]}
            </p>
          </div>

          <div className={`grid gap-6 ${showDetailPanel && selectedCase ? 'lg:grid-cols-2' : 'lg:grid-cols-1'}`}>
            <div>
              <CaseListTable
                cases={filteredCases}
                onCaseSelect={handleCaseSelect}
                selectedCaseId={selectedCaseId}
                language={language}
                direction={direction}
              />
            </div>

            {showDetailPanel && selectedCase && (
              <div className="lg:sticky lg:top-24 lg:self-start">
                <CaseDetailPanel
                  caseData={selectedCase}
                  onClose={handleCloseDetailPanel}
                  onEdit={handleEditCase}
                  onScheduleHearing={handleScheduleHearing}
                  onCreateInvoice={handleCreateInvoice}
                  language={language}
                  direction={direction}
                />
              </div>
            )}
          </div>
        </main>
      </div>
      <CaseFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingCase(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={editingCase}
        language={language}
        direction={direction}
      />
    </div>
  );
};

export default CaseManagement;